import type { FinalizeNpcSessionResponse } from '../../store/api/npcApi';

type NpcLevelOutcome = NonNullable<NonNullable<FinalizeNpcSessionResponse['npc']>['levelResult']>;

interface NpcLevelResultCardProps {
  result: NpcLevelOutcome;
}

/**
 * The level outcome exactly as finalize-npc reports it. XP is shown only for
 * the attempt that newly passed the level (the level authority awards it once);
 * an attempt whose overall was not measured is "not evaluable", never 0%.
 */
export function NpcLevelResultCard({ result }: NpcLevelResultCardProps) {
  const best = typeof result.currentBestScore === 'number' ? Math.round(result.currentBestScore) : null;
  const attempt = typeof result.attemptScore === 'number' ? Math.round(result.attemptScore) : null;
  // The backend can send null (the level could not be read): "not available", never "null%".
  const required = typeof result.requiredScore === 'number' ? `${result.requiredScore}%` : 'a score that is not available right now';
  const evaluable = best !== null;
  const tone = result.passed ? 'green' : evaluable ? 'amber' : 'gray';
  // Static class strings: Tailwind cannot see classes built from variables.
  const styles = {
    green: { box: 'bg-green-50 border-green-200', dot: 'bg-green-100', icon: 'text-green-600', title: 'text-green-800', text: 'text-green-700' },
    amber: { box: 'bg-amber-50 border-amber-200', dot: 'bg-amber-100', icon: 'text-amber-600', title: 'text-amber-800', text: 'text-amber-700' },
    gray: { box: 'bg-gray-50 border-gray-200', dot: 'bg-gray-100', icon: 'text-gray-600', title: 'text-gray-800', text: 'text-gray-700' },
  }[tone];
  const title = result.newlyPassed
    ? 'Level passed!'
    : result.passed
      ? 'Level already passed'
      : evaluable
        ? 'Not yet passed'
        : 'Not evaluable';
  const detail = result.newlyPassed
    ? result.xpAwarded > 0
      ? `You earned +${result.xpAwarded} XP.`
      : 'You passed this level.'
    : result.passed
      ? 'No new XP: this level was already passed.'
      : evaluable
        ? `Need ${required} to pass.${attempt !== null ? ` This attempt scored ${attempt}%.` : ''} Your best is ${best}%.`
        : 'The overall score could not be measured for this attempt, so it neither passes nor fails the level.';
  return (
    <div data-testid="npc-level-result" className={`mt-4 pt-4 border-t rounded-lg p-4 ${styles.box}`}>
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${styles.dot}`}>
          {result.passed ? (
            <span aria-hidden="true" className="text-green-600 text-xl">✓</span>
          ) : (
            <span aria-hidden="true" className={`text-xl ${styles.icon}`}>→</span>
          )}
        </div>
        <div className="flex-1">
          <h4 className={`font-semibold ${styles.title}`}>{title}</h4>
          <p className={`text-sm ${styles.text}`}>{detail}</p>
        </div>
        {evaluable && (
          <div className="text-right">
            <div className="text-lg font-bold text-gray-900">{best}%</div>
            <div className="text-xs text-gray-500">Best score</div>
          </div>
        )}
      </div>
    </div>
  );
}
